import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const title = 'Getting Started';
export const _frontmatter = {};
const layoutProps = {
  title,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h1>
    <p>{`Install the core Rebass library.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm i rebass
`}</code></pre>
    <h2 {...{
      "id": "themeprovider"
    }}>{`ThemeProvider`}</h2>
    <p>{`By default, Rebass components are stylistically unopinionated and do not include a theme.
You can add a theme to your application with a `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` component and by providing a theme in context.
For this guide, use the Emotion `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` with the default Rebass preset theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm i @rebass/preset emotion-theming
`}</code></pre>
    <p>{`Wrap your application with the `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import { ThemeProvider } from 'emotion-theming'
import theme from '@rebass/preset'

export default props =>
  <ThemeProvider theme={theme}>
    {props.children}
  </ThemeProvider>
`}</code></pre>
    <p>{`Alternatively, if you'd like to use Rebass with `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{`,
you can use the `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` from `}<inlineCode parentName="p">{`theme-ui`}</inlineCode>{`, or use `}<a parentName="p" {...{
        "href": "https://theme-ui.com/gatsby-plugin"
      }}><inlineCode parentName="a">{`gatsby-plugin-theme-ui`}</inlineCode></a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`import { ThemeProvider } from 'theme-ui'
`}</code></pre>
    <h2 {...{
      "id": "composition"
    }}>{`Composition`}</h2>
    <p>{`You can use Rebass components out-of-the-box to build larger, composite components.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import {
  Box,
  Card,
  Image,
  Heading,
  Text
} from 'rebass'

export default ({
  image,
  title,
  description
}) =>
  <Box width={256}>
    <Card
      sx={{
        p: 1,
        borderRadius: 2,
        boxShadow: '0 0 16px rgba(0, 0, 0, .25)',
      }}>
      <Image src={image} />
      <Box px={2}>
        <Heading as='h3'>
          {title}
        </Heading>
        <Text fontSize={0}>
          {description}
        </Text>
      </Box>
    </Card>
  </Box>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`  <Box width={256}>
    <Card
      sx={{
        p: 1,
        borderRadius: 2,
        boxShadow: '0 0 16px rgba(0, 0, 0, .25)',
      }}>
      <Image src={props.image} />
      <Box px={2}>
        <Heading as='h3'>
          Card Demo
        </Heading>
        <Text fontSize={0}>
          You can edit this code
        </Text>
      </Box>
    </Card>
  </Box>
`}</code></pre>
    <p>{`All components in Rebass are extensions of the base `}<a parentName="p" {...{
        "href": "/box"
      }}>{`Box`}</a>{` component, which includes an `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop for theme-based styling as well as a standard set of `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{` style props.`}</p>
    <h2 {...{
      "id": "extending"
    }}>{`Extending`}</h2>
    <p>{`You can also use Rebass components as basic building blocks and extend them to create custom UI components,
which is a great way to get started with a new design system without `}<em parentName="p">{`boiling the ocean`}</em>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// custom button example
import React from 'react'
import { Button } from 'rebass'

export default props =>
  <Button
    {...props}
    sx={{
      fontSize: 1,
      textTransform: 'uppercase',
      borderRadius: 99999,
    }}
  />
`}</code></pre>
    <h2 {...{
      "id": "theming"
    }}>{`Theming`}</h2>
    <p>{`Use the default Rebass theme or completely customize the look and feel with a custom theme.
Since Rebass follows the `}<a parentName="p" {...{
        "href": "https://github.com/system-ui/theme-specification"
      }}>{`Theme Specification`}</a>{`, any theme built for use with `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{` or `}<a parentName="p" {...{
        "href": "https://styled-system.com"
      }}>{`Styled System`}</a>{` will work out-of-the-box.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import { ThemeProvider } from 'emotion-theming'

const theme = {
  fontSizes: [
    12, 14, 16, 24, 32, 48, 64
  ],
  colors: {
    primary: '#07c',
    gray: '#f6f6ff',
  },
  buttons: {
    primary: {
      color: 'white',
      bg: 'primary',
    },
    outline: {
      color: 'primary',
      bg: 'transparent',
      boxShadow: 'inset 0 0 0 2px'
    },
  },
}

export default props =>
  <ThemeProvider theme={theme}>
    {props.children}
  </ThemeProvider>
`}</code></pre>
    <h2 {...{
      "id": "variants"
    }}>{`Variants`}</h2>
    <p>{`Each component accepts a `}<inlineCode parentName="p">{`variant`}</inlineCode>{` prop, giving you multiple styles for buttons, cards, text styles, and more.
You can define custom variants in your own theme or use the defaults from the Rebass preset theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Button variant='primary' mr={2}>Beep</Button>
<Button variant='secondary'>Boop</Button>
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      